import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type ConfirmVariant = 'danger' | 'recover' | 'warning' | 'info' | 'save' | 'update';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

const DEFAULT_LABEL_KEYS: Record<ConfirmVariant, string> = {
  danger: 'COMMON.DELETE',
  recover: 'COMMON.RESTORE',
  warning: 'COMMON.CONTINUE',
  info: 'OK',
  save: 'COMMON.SAVE',
  update: 'COMMON.UPDATE',
};

@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly translate = inject(TranslateService);

  readonly visible = signal(false);
  readonly options = signal<ConfirmOptions>({
    title: '',
    message: '',
  });

  private resolveFn: ((value: boolean) => void) | null = null;

  confirm(opts: ConfirmOptions): Promise<boolean> {
    const variant = opts.variant || 'danger';
    this.options.set({
      cancelLabel: this.translate.instant('COMMON.CANCEL'),
      confirmLabel: this.translate.instant(DEFAULT_LABEL_KEYS[variant]),
      ...opts,
      variant,
    });
    this.visible.set(true);

    return new Promise<boolean>((resolve) => {
      this.resolveFn = resolve;
    });
  }

  accept() {
    this.visible.set(false);
    this.resolveFn?.(true);
    this.resolveFn = null;
  }

  cancel() {
    this.visible.set(false);
    this.resolveFn?.(false);
    this.resolveFn = null;
  }
}
